/**
 * THE CALLER'S SIDE OF LIVE AUDIO: the `/ws/live` socket. See docs/hitl.md §7.
 *
 * Opened when the DO sends `{t:'live', on:true}` — somebody in the panel is
 * listening — and closed on `on:false`. While open it sends two voices as live
 * frames, both tapped on the call's own 48 kHz AudioContext:
 *
 *   Caller   the caller's mic, after the browser's echo cancelling, so it is
 *            what the agent hears. A muted mic, or the greeting hold, sends
 *            silence rather than nothing, so the listener's timeline keeps
 *            moving and the frame count stays honest.
 *   Agent    the agent's voice as this page plays it. Stopped at a takeover:
 *            there is no agent any more.
 *
 * After a takeover the same socket brings the person's voice back as `Human`
 * frames, played through the shared player and its echo-cancelled loopback.
 *
 * IT IS A COPY, AND ONLY A COPY. Media for the conversation is still
 * browser <-> OpenAI until a takeover. So this socket reconnects with backoff
 * while `on` and never touches the call when it fails: a listener who hears
 * nothing is told so by the DO, and the caller notices nothing.
 *
 * SEQ COUNTS PRODUCED FRAMES, NOT SENT ONES. A frame made while the socket is
 * down is dropped but still numbered, so the DO sees the gap as missing frames
 * (silence of the right length in the takeover recording) instead of time
 * that never happened.
 */
import { LiveSource, decodeLiveFrame, encodeLiveFrame } from '@voiceai/wire'
import { LivePlayer, LiveTap } from './audio'
import type { LiveLog } from './audio'

const RECONNECT_MIN_MS = 250
const RECONNECT_MAX_MS = 8000
/**
 * Past this much unsent data, new frames are dropped rather than queued. About
 * a second of both voices: live audio that is a second late is still worth
 * hearing, audio that is ten seconds late is not.
 */
const MAX_BUFFERED_BYTES = 64 * 1024

export interface LiveLinkOptions {
  /** `liveUrl` with the session's reconnect token in `t` (see socketUrl). */
  url: string
  ctx: AudioContext
  mic: MediaStream
  /** True while the caller is muted or held silent by the greeting. */
  micSilenced(): boolean
  onLog: LiveLog
}

export class LiveLink {
  private ws: WebSocket | null = null
  private closed = false
  private attempt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private readonly seq: Record<number, number> = { [LiveSource.Caller]: 0, [LiveSource.Agent]: 0 }
  private callerTap: LiveTap | null = null
  private agentTap: LiveTap | null = null
  private agentTapOpening = false
  /** The agent's stream, kept so a tap can be made on it whenever it is wanted. */
  private agentStream: MediaStream | null = null
  private agentWanted = true
  private started = false
  private player: LivePlayer | null = null
  private playerOpening: Promise<MediaStream | null> | null = null
  private loggedBadFrame = false
  private loggedEarlyHuman = false

  constructor(private readonly opts: LiveLinkOptions) {}

  /** Open the socket and start the taps. Idempotent. */
  start(): void {
    if (this.closed || this.started) return
    this.started = true
    this.open()
    void LiveTap.open(this.opts.ctx, this.opts.mic, (pcm) => this.send(LiveSource.Caller, pcm), this.opts.micSilenced)
      .then((tap) => {
        if (this.closed) tap.stop()
        else this.callerTap = tap
      })
      .catch((e: unknown) => this.opts.onLog('error', `the caller's voice cannot be sent to a listener: ${String(e)}`))
    this.tapAgent()
  }

  /** The agent's stream arrived (or was already there when live audio began). */
  setAgentStream(stream: MediaStream): void {
    this.agentStream = stream
    this.tapAgent()
  }

  /** A takeover: the agent is gone, so its copy stops for good. */
  stopAgent(): void {
    this.agentWanted = false
    this.agentTap?.stop()
    this.agentTap = null
  }

  /**
   * A takeover: start playing the person's voice. Resolves with the played
   * sound as a stream when it goes through the loopback (for a level meter),
   * otherwise null. Idempotent.
   */
  playHuman(): Promise<MediaStream | null> {
    this.playerOpening ??= LivePlayer.open(this.opts.ctx, this.opts.onLog).then(
      (player) => {
        if (this.closed) {
          player.close()
          return null
        }
        this.player = player
        return player.stream
      },
      (e: unknown) => {
        this.opts.onLog('error', `the person's voice cannot be played: ${String(e)}`)
        return null
      },
    )
    return this.playerOpening
  }

  close(): void {
    if (this.closed) return
    this.closed = true
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.callerTap?.stop()
    this.callerTap = null
    this.agentTap?.stop()
    this.agentTap = null
    this.player?.close()
    this.player = null
    const ws = this.ws
    this.ws = null
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      try {
        ws.close(1000, 'live audio off')
      } catch {
        /* nothing to do about a socket that will not close */
      }
    }
  }

  private tapAgent(): void {
    const stream = this.agentStream
    if (!this.started || this.closed || !this.agentWanted || !stream || this.agentTap || this.agentTapOpening) return
    // Claimed before the await, so a second call while this one opens cannot
    // make a second tap; a takeover that lands meanwhile stops the tap it made.
    this.agentTapOpening = true
    void LiveTap.open(this.opts.ctx, stream, (pcm) => this.send(LiveSource.Agent, pcm)).then(
      (tap) => {
        this.agentTapOpening = false
        if (this.closed || !this.agentWanted) tap.stop()
        else this.agentTap = tap
      },
      (e: unknown) => {
        this.agentTapOpening = false
        this.opts.onLog('error', `the agent's voice cannot be sent to a listener: ${String(e)}`)
      },
    )
  }

  private send(source: LiveSource, pcm: Int16Array): void {
    const seq = this.seq[source] ?? 0
    this.seq[source] = seq + 1
    const ws = this.ws
    if (!ws || ws.readyState !== WebSocket.OPEN || ws.bufferedAmount > MAX_BUFFERED_BYTES) return
    try {
      ws.send(encodeLiveFrame(source, seq, pcm))
    } catch {
      /* a socket closing under us; the close handler reconnects */
    }
  }

  private open(): void {
    if (this.closed) return
    const ws = new WebSocket(this.opts.url)
    ws.binaryType = 'arraybuffer'
    this.ws = ws
    ws.onopen = () => {
      this.attempt = 0
      this.opts.onLog('info', 'live audio socket open')
    }
    ws.onmessage = (ev: MessageEvent) => {
      if (!(ev.data instanceof ArrayBuffer)) return
      let frame
      try {
        frame = decodeLiveFrame(ev.data)
      } catch (e) {
        if (!this.loggedBadFrame) {
          this.loggedBadFrame = true
          this.opts.onLog('warn', `a live frame from the server could not be read: ${String(e)}`)
        }
        return
      }
      if (frame.source !== LiveSource.Human) return
      // Only after the takeover said so. The two arrive on different sockets,
      // so a frame can in principle beat the message; it is dropped, which
      // costs the first 20 ms at most.
      if (!this.player) {
        if (!this.loggedEarlyHuman && this.playerOpening === null) {
          this.loggedEarlyHuman = true
          this.opts.onLog('info', 'a person’s voice arrived before the takeover did, and was dropped')
        }
        return
      }
      this.player.push(LiveSource.Human, frame.pcm)
    }
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null
      this.scheduleReconnect()
    }
    // No detail on a WebSocket error by design; onclose covers it.
    ws.onerror = () => {}
  }

  private scheduleReconnect(): void {
    if (this.closed || this.reconnectTimer !== null) return
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** this.attempt)
    this.attempt += 1
    // Jitter, for the reason in control.ts: a DO deploy drops every socket at once.
    const jittered = delay * (0.7 + Math.random() * 0.6)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.open()
    }, jittered)
  }
}
